/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {CustomButtonCellComponent} from '../../../core/components/custom-button-cell/custom-button-cell.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../core/state/state.service';
import {MarketAreaPlanningMarketMap} from '../../../models/market-area-planning-market-map.model';
import {PlanningMarket} from '../../../models/planning-market.model';
import {User} from '../../../models/user.model';
import {MarketAreaService} from '../../../services/market-area.service';
import {PlanningMarketService} from '../../../services/planning-market.service';
import {AddPlanningMarketPopUpComponent} from './add-planning-market-pop-up/add-planning-market-pop-up.component';
import {EditPlanningMarketPopUpComponent} from './edit-planning-market-pop-up/edit-planning-market-pop-up.component';

@Component({
    selector: 'app-manage-planning-market',
    templateUrl: './manage-planning-market.component.html',
    styleUrls: ['./manage-planning-market.component.scss'],
    standalone: false
})
export class ManagePlanningMarketComponent implements OnInit {
  showSpinner: boolean;
  planningMarketList: PlanningMarket[];
  dataSource: MatTableDataSource<PlanningMarket> = new MatTableDataSource(null);
  displayedColumns: string[];
  planningMarketColumnHeaders: ColDef[] = [
    {field: 'plngMkt', headerName: 'Planning Market', filter: AutocompleteFilterComponent},
    {field: 'subRegion', headerName: 'Subregion', filter: AutocompleteFilterComponent},
    {field: 'active', headerName: 'Active Status', valueFormatter: (params) => params.value ? 'Active' : 'Inactive'},
    {
      field: '',
      cellRenderer: CustomButtonCellComponent,
      cellRendererParams: {
        icon: 'edit',
        onClick: this.editPlanningMarket.bind(this)
      }
    }
  ];
  user: User;
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  constructor(private readonly planningMarketService: PlanningMarketService,
              private readonly marketAreaService: MarketAreaService,
              public dialog: MatDialog,
              public snackBar: FabMessageService,
              private readonly userService: StateService) {
  }

  ngOnInit(): void {
    this.setVariables();
    this.getPlanningMarkets();
  }

  /**
   *  sets initial values for variables on init
   */
  setVariables(): void {
    this.user = this.userService.user;
    this.showSpinner = false;
  }

  /**
   * Get all Planning Market Records
   */
  getPlanningMarkets(): void {
    this.showSpinner = true;
    this.planningMarketService.getPlanningMarkets().subscribe(
      (data) => {
        this.planningMarketList = data;
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.showSpinner = false;
      }
    );
  }

  /**
   * Opens a dialog pop up box to be able to add a new planning market record on click of the add button
   */
  onAddButtonClick(): void {
    const dialogRef = this.dialog.open(AddPlanningMarketPopUpComponent);
    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        const marketAreaMap: MarketAreaPlanningMarketMap = {
          mktArea: res.data.marketArea,
          subMktArea: '',
          plngMkt: res.data.planningMarketName,
        }
        const planningMarket: PlanningMarket = {
          id: null,
          tstamp: null,
          plngMkt: res.data.planningMarketName,
          subRegion: res.data.subregion,
          active: 1,
          userId: this.user.gmid
        }
        this.addPlanningMarket(planningMarket, marketAreaMap);
      }
    });
  }

  /**
   * Opens a dialog pop up box to be able to update selected planning market record
   * @param planningMarket
   */
  editPlanningMarket(planningMarket: PlanningMarket): void {
    const dialogRef = this.dialog.open(EditPlanningMarketPopUpComponent,
      {
        data: {
          plngMkt: planningMarket
        }
      });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.showSpinner = true;
        this.planningMarketService.updatePlanningMarket(res.data).subscribe({
          error: (err) => {
            this.showSpinner = false;
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.snackBar.displaySuccess(`Updated ${res.data.plngMkt}`, DISMISS_MSG);
            this.getPlanningMarkets();
          }
        });
      }
    });
  }

  /**
   * Adds a new planning market
   * @param planningMarket The planning market object to add
   * @param map
   */
  addPlanningMarket(planningMarket: PlanningMarket, map: MarketAreaPlanningMarketMap): void {
    this.planningMarketService.addPlanningMarket(planningMarket).subscribe({
      error: (err) => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.snackBar.displaySuccess(`Added new planning market`, DISMISS_MSG);
        this.addMarketAreaPlanningMarketMap(map);
        this.getPlanningMarkets();
      }
    });
  }

  /**
   * Adds a new market area planning market mapping
   * @param map The mapping to add
   */
  addMarketAreaPlanningMarketMap(map: MarketAreaPlanningMarketMap): void {
    this.marketAreaService.addPlanningMarketMapping([map]).subscribe({
      error: () => {
        this.showSpinner = false;
        this.snackBar.displayError('unable to create mapping. please go to manage -> market area/planning market ' +
          'grouping and add the planning market to a market area', DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
      }
    });
  }
}
